import React, { useContext } from "react";
import { Form } from "react-final-form";
import arrayMutators from "final-form-arrays";
import { ChevronRight, ChevronLeft, Info, MapPin } from "lucide-react";

import { IntlContext } from "context";
import formatErrorResponse from "helpers/form/formatErrorResponse";
import { getErrorResponseData } from "helpers/typing";
import { Textarea } from "components/Form/Field";
import Button from "ui/Button";
import Address from "components/Form/Address";
import { useRegisterStepper } from "../common/RegisterStepper";
import type { RegisterDraft } from "../types";
import { useRegisterDraftGuard } from "../common/useRegisterDraftGuard";
import { trackRegisterStepComplete } from "lib/analytics/dataLayer";

interface Props {
    client: RegisterDraft | undefined;
    setClient: (client: RegisterDraft) => void;
}

const RegisterClientNeed: React.FC<Props> = ({ client, setClient }) => {
    const intl = useContext(IntlContext);
    const stepper = useRegisterStepper();
    useRegisterDraftGuard(client, "need");

    const isNonEmptyString = (value: unknown) => String(value ?? "").trim().length > 0;

    const onSubmit = async (values: Record<string, unknown>) => {
        try {
            setClient(Object.assign({}, client, values) as RegisterDraft);
            trackRegisterStepComplete({
                registerType: "client",
                stepKey: "need",
                stepIndex: 2,
                stepLabel: intl.formatMessage("authentication.register.client_flow.need.label"),
            });
            stepper.goTo("informations");
        } catch (err: unknown) {
            const errData = getErrorResponseData(err);
            if (errData) {
                return formatErrorResponse(errData);
            }
            throw err;
        }
    };

    return (
        <React.Fragment>
            <Form
                onSubmit={onSubmit}
                initialValues={client}
                mutators={{ ...arrayMutators }}
                render={({ handleSubmit, submitting, form: _form, values }) => {
                    const descriptionLength = String(values?.description ?? "").length;
                    const descriptionMinLength = 30;
                    const descriptionMaxLength = 2000;
                    const hasMinDescriptionLength = descriptionLength >= descriptionMinLength;

                    const hasAddress =
                        isNonEmptyString(values?.address) &&
                        isNonEmptyString(values?.coordinates_lat) &&
                        isNonEmptyString(values?.coordinates_lon) &&
                        isNonEmptyString(values?.house_number) &&
                        isNonEmptyString(values?.street) &&
                        isNonEmptyString(values?.postcode) &&
                        isNonEmptyString(values?.city) &&
                        isNonEmptyString(values?.commune_code);

                    const canGoNext = hasMinDescriptionLength && hasAddress;

                    return (
                    <form onSubmit={handleSubmit} className="register__form">
                        <div className="flex flex--direction-column gap-10">
                            <div>
                                <p className="h1 font-black">
                                    Décrivez <span className="text-primary">votre besoin</span>
                                </p>
                                <p className="h4 mt-4">
                                    Plus vous serez précis, plus vous recevrez de propositions adaptées.
                                </p>
                            </div>

                            <div className="register__group-fields space-y-4">
                                <Textarea
                                    inputProps={{
                                        name: "description",
                                        minLength: descriptionMinLength,
                                        maxLength: descriptionMaxLength,
                                        rows: 6,
                                        placeholder:
                                            "Exemple : J'ai besoin d'une personne pour tondre ma pelouse de 200m² et tailler les haies chaque semaine...",
                                        className:
                                            "field-control__input min-h-[160px] resize-none rounded-[22px] border border-input bg-background px-6 py-5 text-sm placeholder:text-muted-foreground focus-visible:ring-2 focus-visible:ring-ring",
                                    }}
                                    labelProps={{
                                        label: "",
                                    }}
                                />

                                <div className="flex w-full items-center justify-end gap-4 text-[11px] font-bold uppercase tracking-wider text-muted-foreground text-right">
                                    <div className="flex items-center gap-4">
                                        <div>
                                            <span className="text-primary">{descriptionLength}</span>/{descriptionMaxLength} caractères
                                        </div>
                                        <div>
                                            Min. <span className={hasMinDescriptionLength ? "text-emerald-600" : "text-red-600"}>{descriptionMinLength}</span> caractères
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="w-full rounded-[28px] border border-primary/10 bg-primary/5 px-6 py-5 text-xs text-muted-foreground">
                                <div className="flex items-center gap-3">
                                    <div className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-primary shadow-sm ring-1 ring-border/40">
                                        <Info className="h-4 w-4" />
                                    </div>
                                    <p className="min-w-0 leading-relaxed">
                                        Toute description soumise doit être conforme à nos Conditions Générales
                                        d'Utilisation. Nous nous réservons le droit de refuser ou de ne pas publier tout
                                        contenu jugé inapproprié, non conforme ou contraire à ces conditions.
                                    </p>
                                </div>
                            </div>

                            <div className="space-y-6">
                                <div>
                                    <div className="inline-flex items-center gap-2 text-base font-black">
                                        <MapPin className="h-4 w-4" />
                                        Localisation
                                    </div>
                                    <p className="mt-1 text-sm text-muted-foreground">
                                        Les prestataires ne verront pas l'adresse exacte tant que la prestation ne sera
                                        pas validée
                                    </p>
                                </div>

                                <div className="register__group-fields">
                                    <div className="text-xs font-black uppercase tracking-wider text-muted-foreground">
                                        Adresse postale *
                                    </div>
                                    <div className="mt-2">
                                        <Address
                                            inputProps={{
                                                name: "address",
                                                latitudeName: "coordinates_lat",
                                                longitudeName: "coordinates_lon",
                                                houseNumber: "house_number",
                                                street: "street",
                                                postcode: "postcode",
                                                city: "city",
                                                commune_code: "commune_code",
                                                required: true,
                                                placeholder: "ex. 12 rue de la Paix 44000 Nantes",
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="flex items-center justify-between text-center margin-top--10">
                                <Button
                                    type="button"
                                    className="btn inline-flex items-center justify-center gap-3 rounded-full px-0 py-4 font-black text-muted-foreground/70"
                                    onClick={() => {
                                        setClient(Object.assign({}, client, values));
                                        stepper.goTo("services");
                                    }}
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                    {intl.formatMessage("generic.step.previous")}
                                </Button>
                                <Button
                                    className="btn btn--primary inline-flex items-center justify-center gap-3 rounded-full font-black px-10 py-4"
                                    type="submit"
                                    disabled={submitting || !canGoNext}
                                >
                                    {intl.formatMessage("generic.step.next")}
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </form>
                    );
                }}
            />
        </React.Fragment>
    );
};

export default RegisterClientNeed;

